import { getTotalPrice, multiplyMoney } from '@shared/lib/money';
import * as React from 'react';
import CheckoutAdditionalService from '../..';
import { useSelector } from 'react-redux';
import { getPriorityBoarding, getSegments } from '../../../Checkout/store/order/selectors';
import { useTranslation } from 'react-i18next';
import { Money as MoneyType } from '../../../types';
import { getPassengerState } from '../../../Checkout/store/passengers/selectors';
import MediaQuery from 'react-responsive';
import { MOBILE_MIN_WIDTH } from '../../../utils';
import Modal from '../../../Modal';
import VipServiceSelect from '../../../VipServiceSelect';
import SlideBottom from '../../../SlideBottom';
import { useTheme } from '../../../theme';
import { useRenderers } from '../../../renderProps';
import { AdditionalServiceType } from '../../types';
import { OrderAdditionalServiceGdsServiceServiceType } from '@websky/graphql';
import { useOrderService } from '../hooks';
import { useIsCanModifyServices } from '../../hooks';
import { closeRender } from '../Close/Close';
import { ServiceSummary } from '../../../features/service-summary';
import { SafeHtml } from '@shared/ui';

const PriorityBoarding: React.FC = props => {
	const { t } = useTranslation('Checkout');

	const passengers = useSelector(getPassengerState);
	const segments = useSelector(getSegments);
	const priorityServices = useSelector(getPriorityBoarding);

	const {
		isLoading,
		opened,
		onOpen,
		onClose,
		selectedServices,
		renderSubheader,
		onSaveHandler,
		onRemoveCartService
	} = useOrderService(priorityServices, OrderAdditionalServiceGdsServiceServiceType.Vip);

	const theme = useTheme('CheckoutAdditionalService').PriorityBoarding;

	const selectedPriorityPrice = React.useMemo(() => {
		const prices: MoneyType[] = [];

		passengers.forEach(passenger => {
			priorityServices.forEach(service => {
				if (selectedServices[passenger.id].hasOwnProperty(service.id)) {
					selectedServices[passenger.id][service.id].forEach(selectedService => {
						prices.push(multiplyMoney(service.price, selectedService.segments.length));
					});
				}
			});
		});

		return getTotalPrice(prices, price => price);
	}, [selectedServices, priorityServices, passengers]);

	const isCanModifyServices = useIsCanModifyServices(priorityServices);

	const minPrice = React.useMemo(() => {
		let price = priorityServices[0].price;

		priorityServices.forEach(service => {
			if (service.price.amount < price.amount) {
				price = service.price;
			}
		});

		return price;
	}, [priorityServices]);

	const AdditionalServiceRender = useRenderers()?.CheckoutAdditionalService?.CheckoutAdditionalService;
	const ServiceWrapper = AdditionalServiceRender ?? CheckoutAdditionalService;

	return (
		<>
			<ServiceSummary serviceType="PriorityBoarding" onDelete={onRemoveCartService}>
				{content => (
					<ServiceWrapper
						header={t('Priority boarding')}
						description={priorityServices[0].description}
						isSelected={!!content}
						addClick={onOpen}
						priceTotal={selectedPriorityPrice}
						priceFrom={minPrice}
						className={theme.priority}
						buttons={[
							{
								action: onOpen,
								text: isCanModifyServices ? t('Change') : t('More details'),
								buttonType: 'change',
								isLoading
							}
						]}
						serviceType={AdditionalServiceType.PriorityBoarding}
						addButtonText={!isCanModifyServices ? t('More details') : null}
						isLoading={isLoading}
					>
						{content}
					</ServiceWrapper>
				)}
			</ServiceSummary>

			<MediaQuery minWidth={MOBILE_MIN_WIDTH}>
				<Modal
					className={theme.priorityBoardingModal}
					classes={{ paper: theme.paper }}
					open={opened}
					onClose={onClose}
					maxWidth="sm"
					closeButtonRenderer={closeRender}
				>
					<VipServiceSelect
						header={t('Priority boarding')}
						headerClassName={theme.header}
						passengers={passengers}
						services={priorityServices}
						segments={segments}
						onSave={onSaveHandler}
						selectedServices={selectedServices}
						onClose={onClose}
						subheader={
							<>
								{priorityServices[0].vipFullDescription && (
									<SafeHtml tag="div" html={priorityServices[0].vipFullDescription} />
								)}

								{renderSubheader()}
							</>
						}
						showTotalPrice
						showControlPrice
						type="checkbox"
						readOnly={!isCanModifyServices}
					/>
				</Modal>
			</MediaQuery>

			<MediaQuery maxWidth={MOBILE_MIN_WIDTH}>
				<SlideBottom isOpen={opened} onClose={onClose}>
					<VipServiceSelect
						header={t('Priority boarding')}
						passengers={passengers}
						services={priorityServices}
						segments={segments}
						onSave={onSaveHandler}
						selectedServices={selectedServices}
						onClose={onClose}
						subheader={
							<>
								{priorityServices[0].vipFullDescription && (
									<SafeHtml tag="div" html={priorityServices[0].vipFullDescription} />
								)}

								{renderSubheader()}
							</>
						}
						readOnly={!isCanModifyServices}
					/>
				</SlideBottom>
			</MediaQuery>
		</>
	);
};

export default PriorityBoarding;
